import { useCallback, useEffect, useState } from "react";
import { UserCircleGear, Check, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELD_LABELS = {
  full_name: "Full Name",
  phone: "Phone",
  emergency_contact: "Emergency Contact",
  bank_name: "Bank Name",
  bank_account: "Bank Account",
  date_of_hire: "Date of Hire",
};

function ProfileRequests() {
  const [reqs, setReqs] = useState([]);
  const [busyId, setBusyId] = useState("");

  const refresh = useCallback(() => {
    api.get("/profile/requests").then((r) => setReqs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const resolve = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/profile/requests/${id}/resolve`, { action });
      toast.success(action === "approve" ? "Profile update approved and applied" : "Profile update rejected");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-5" data-testid="profile-requests-section">
      <div>
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
          <UserCircleGear size={24} className="text-primary" /> Profile Update Requests
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Employee profile edits are held here — changes apply only after you approve them.
        </p>
      </div>
      {reqs.length === 0 ? (
        <div className="rounded-sm border border-dashed border-input p-12 text-center" data-testid="profile-requests-empty">
          <UserCircleGear size={32} className="mx-auto text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">No pending profile update requests.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reqs.map((r) => (
            <div key={r.id} className="rounded-sm border border-border bg-card p-5" data-testid={`profile-request-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-lg font-bold">{r.name}</p>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{r.role} · {r.email}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`profile-request-approve-${r.id}`} disabled={busyId === r.id}
                    onClick={() => resolve(r.id, "approve")}
                    className="rounded-sm bg-emerald-600 font-bold uppercase tracking-wider text-white hover:bg-emerald-500">
                    <Check size={14} className="mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`profile-request-reject-${r.id}`} disabled={busyId === r.id}
                    onClick={() => resolve(r.id, "reject")}
                    className="rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-px border border-border/60 bg-border/40 md:grid-cols-2">
                {Object.keys(FIELD_LABELS).filter((f) => f in r.requested).map((f) => (
                  <div key={f} className="bg-background p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{FIELD_LABELS[f]}</p>
                    <p className="mt-1 text-xs text-red-400 line-through">{r.current[f] || "—"}</p>
                    <p className="text-sm font-semibold text-emerald-400">{r.requested[f] || "—"}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ApprovalsPanel() {
  return (
    <div className="space-y-10" data-testid="approvals-panel">
      <ProfileRequests />
    </div>
  );
}
